import axios from "axios";
import { APIOverrides } from "../../ApiOverrides"
import type { FlashMarketFeeProps, PollCampaignLeaderProps, PollCampaignProps, PollOptionProps, PollProps, PollResponseProps, PollSummaryProps } from "../../types";
import moment from "moment-mini";

//let EVENT_SVC_API = ''
let AUTH_SVC_API = ''
//let MK_SVC_API = ''
//let SOCIAL_SVC_API = ''

export { FlashMarketApi, FlashAdminApi }



const FlashMarketApi = {
    setEnvironment: () => {
        const endpoints = APIOverrides.getEndpoints();
        //EVENT_SVC_API = endpoints['EVENT_SVC_API'] as string;
        //MK_SVC_API = endpoints['MK_SVC_API'] as string;
        AUTH_SVC_API = endpoints['AUTH_SVC_API'] as string;
        //SOCIAL_SVC_API = endpoints['SOCIAL_SVC_API'] as string;
    },
    getPollById : async(poll_id:string):Promise<undefined | { poll:PollProps, poll_options:PollOptionProps[], poll_summaries: PollSummaryProps[] }> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/polls/poll/${poll_id}`)
            const { poll, poll_options, poll_summaries } = resp.data;
            return { poll, poll_options, poll_summaries }
        } catch (e) {
            return undefined
        }       
    },
    getPollsByCampaignId: async(poll_campaign_id:string):Promise<PollProps[]> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/polls/campaign/${poll_campaign_id}`);
            return resp.data.polls
        } catch (e) {
            return []
        }
    },
    getActiveFlashMarkets: async():Promise<PollProps[]> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v2/pm/flash/active`);
            return resp.data.polls
        } catch (e) {
            console.log(e);
            return []
        }
    },
    getActiveMarkets: async(company_id?:string):Promise<{polls:PollProps[], poll_campaigns:PollCampaignProps[]}> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v2/pm/active?company_id=${company_id}`);
            return resp.data
        } catch (e) {
            return { polls: [], poll_campaigns: [] }
        }
    },
    getActiveLinkedFlashMarkets: async(league_id?:string, contest_type?:string, contest_id?:string, side_type?:string, side_id?:string):Promise<PollProps[]> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v2/pm/flash/linked?league_id=${league_id}&contest_type=${contest_type}&contest_id=${contest_id}&side_type=${side_type}&side_id=${side_id}`);
            return resp.data.polls
        } catch (e) {
            console.log(e);
            return []
        }
    },
    getPollCampaignById : async(poll_campaign_id:string):Promise<{ poll_campaign:PollCampaignProps, flash_market_fees:FlashMarketFeeProps[] } | undefined> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/polls/campaigns/campaign/${poll_campaign_id}`)
            return resp.data
        } catch (e) {
            return undefined
        }

    },
    getMyCampaignResponses: async(poll_campaign_id:string):Promise<PollResponseProps[]> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/polls/campaigns/responses/${poll_campaign_id}`)
            return resp.data.poll_responses
        } catch (e) {
            return []
        }
    },
    createPMOrder: async(poll_option_id:string, stake:number, poll_response_id?:string):Promise<{ poll_response:PollResponseProps, my_leader:PollCampaignLeaderProps, poll_campaign:PollCampaignProps } | undefined> => {
        try {
            const resp = await axios.post(`${AUTH_SVC_API}/v2/pm/action/order`, { poll_option_id, stake, poll_response_id });
            return resp.data
        } catch (e) {
            console.log(e);
            return undefined
        }
    },
    
}

const FlashAdminApi = {
    updatePoll: async(poll:PollProps):Promise<PollProps | undefined> => {
        try {
            const resp = await axios.post(`${AUTH_SVC_API}/v1/polls/poll/update`, { poll });
            return resp.data.poll
        } catch (error) {
            return undefined
        }
    },
    closePoll : async(poll_id:string, winning_option_id?:string, winning_value?:string):Promise<PollProps|undefined> => {
        try {
            const resp = await axios.post(`${AUTH_SVC_API}/v1/polls/poll/close`, { poll_id, winning_option_id, winning_value })
            return resp.data.poll.poll
        } catch (e) {
            return undefined
        }
    },
}

export const FlashMarketHelpers = {
    toWin: (stake:number, parimutuel_odds?:number):number => {
        if(!parimutuel_odds){ return 0 }
        return stake / parimutuel_odds
    },
    updatePollOptionProbability: (
    changingOptionId: string,
    newProbability: number,
    allOptions: PollOptionProps[]
): PollOptionProps[] => {
    const total = 1;
    const minProb = 0.001; // minimum probability for unlocked options

    // Clone and update changing option
    const updatedOptions = allOptions.map(o =>
        o.poll_option_id === changingOptionId
            ? { ...o, initial_odds: newProbability }
            : { ...o }
    );

    const lockedOptions = updatedOptions.filter(o => o.probability_locked);
    const unlockedOptions = updatedOptions.filter(
        o => !o.probability_locked && o.poll_option_id !== changingOptionId
    );

    const lockedSum = lockedOptions.reduce(
        (sum, o) => sum + (o.initial_odds ?? 0),
        0
    );

    const changedOption = updatedOptions.find(o => o.poll_option_id === changingOptionId);
    if (!changedOption) return updatedOptions;

    const oldProb = changedOption.initial_odds ?? 0;
    //const delta = newProbability - oldProb; // positive if increasing, negative if decreasing

    // Calculate the maximum allowed increase for the changing option
    const maxForChanged = total - lockedSum - unlockedOptions.length * minProb;
    changedOption.initial_odds = Math.min(Math.max(minProb, newProbability), maxForChanged);

    const adjustedDelta = changedOption.initial_odds - oldProb;

    // Remaining probability to redistribute among unlocked options
    let remaining = total - lockedSum - changedOption.initial_odds;

    if (unlockedOptions.length === 0) return updatedOptions;

    // Redistribute probabilities
    const finalOptions = updatedOptions.map(o => {
        if (o.probability_locked || o.poll_option_id === changingOptionId) return o;

        if (adjustedDelta < 0) {
            // Changing option decreased → distribute evenly to others
            const evenly = remaining / unlockedOptions.length;
            return { ...o, initial_odds: Math.max(minProb, evenly) };
        } else {
            // Changing option increased → reduce proportionally
            const sumUnlocked = unlockedOptions.reduce((sum, u) => sum + (u.initial_odds ?? 0), 0);
            const proportion = (o.initial_odds ?? 0) / sumUnlocked;
            const newOdds = Math.max(minProb, proportion * remaining);
            return { ...o, initial_odds: newOdds };
        }
    });

    // Fix rounding errors
    const currentSum = finalOptions.reduce((sum, o) => sum + (o.initial_odds ?? 0), 0);
    const roundingError = total - currentSum;

    if (roundingError !== 0) {
        const target = finalOptions.find(o => !o.probability_locked && o.poll_option_id !== changingOptionId);
        if (target) {
            target.initial_odds = Math.max(minProb, (target.initial_odds ?? 0) + roundingError);
        }
    }

    return finalOptions;
},
    isNewValid: (options:PollOptionProps[], summaries:PollSummaryProps[], poll?:PollProps) => {
        let errors:string[] = []
        if(!poll){ return ['Waiting for market'] }
        options.map(o => {
            if(!o.option_name){errors.push('Option must have a valid name')  }
        })
        if(options.length <= 1){ errors.push('Need at least 2 options') }
        let total_prob = options.reduce((a,b) => a + (b.initial_odds ?? 0), 0);
        let total_stake = summaries.reduce((a,b) => a + b.count, 0)
        if(!poll.poll_question){ errors.push('Please enter a question') }
        if(poll.max_stake < 1){ errors.push('Max stake must be at least $1') }
        if(poll.minimum_stake < 0.1){ errors.push('Min stake must be greater than 10 cents') }
        if(moment().isAfter(moment(poll.end_datetime))){ errors.push('Market close time is in the past') }
        if(poll.status != 'pending'){ errors.push('Only pending markets can be activated') }
        if(total_stake > 0){ errors.push('Cannot activate with existing responses') }
        if(total_prob.toFixed(2) != '1.00'){ errors.push('Initial probabilities do not add to 1') }
        return errors  
    },
    getResponseStats: (option:PollOptionProps, poll_response?:PollResponseProps) => {
        let empty_reponse = { stake: 0, potential_winnings: 0, winnings: 0, earnings:0 }
        if(!poll_response){ return empty_reponse }
        let num_stake = parseFloat(poll_response.stake as string);
        let pot_winnings = FlashMarketHelpers.calcPotentialWinnings(option.parimutuel_odds, poll_response.stake as string);
        let winnings = poll_response.winnings
        let earnings = winnings - num_stake
        return { stake: num_stake, potential_winnings: pot_winnings, winnings, earnings }
    },
    calcPotentialWinnings: (probability?:number, stake?:string) => {
        if(!probability || !stake){ return 0 }
        let stake_num = parseFloat(stake);
        if(isNaN(stake_num)){ return 0 }
        return stake_num / probability
    },
    calcDraftStake: (poll_responses:PollResponseProps[], draft_stake:string, poll_option_id?:string) => {
        let stake_num = parseFloat(draft_stake);
        let existing_response = poll_responses.find(pr => pr.poll_option_id == poll_option_id);
        if(existing_response){ stake_num += parseFloat(existing_response.stake as string) }
        return stake_num 
    },
    getFeeFromValue: (value:number, fees:FlashMarketFeeProps[]) => {
        let fee = fees.find(f => value >= f.minimum_stake && value <= f.maximum_stake);
        if(!fee){ return fees[0] }
        return fee
    },
    getStats: (poll_summaries:PollSummaryProps[], fees:FlashMarketFeeProps[]) => {
        let total_staked = poll_summaries.reduce((a,b) => a + b.stake, 0);
        let fee = FlashMarketHelpers.getFeeFromValue(total_staked, fees);
        if(!fee){ return { total_staked } }
        total_staked = total_staked * (1 - fee.fee_pct)
        return { total_staked }
    },
    isValid: (poll_responses:PollResponseProps[], poll?:PollProps, stake?:string, poll_option_id?:string) => {
        if(!poll || !stake){ return false }
        if(poll.status != 'active'){ return false }
        if(moment().isAfter(poll.end_datetime) && !poll.manual_close_only){ return false }
        let stake_num = parseFloat(stake);
        let existing_response = poll_responses.find(pr => pr.poll_option_id == poll_option_id);
        if(existing_response){ stake_num += parseFloat(existing_response.stake as string) }
        if(isNaN(stake_num)){ return false }
        if(stake_num > poll.max_stake){ return false }
        if(stake_num < poll.minimum_stake){ return false }
        return true
    }
}